/**
 * Typed transport errors for the Dayvise client protocol (prompt.md §47, §75).
 *
 * WHY this is its own module: every client (web, desktop, Android, and any
 * future CLI) needs to react to the *same* distinctions — "your session is gone"
 * must sign the user out, "entitlement denied" must open the upgrade path, and
 * "the platform is briefly broken" must be retried rather than surfaced as a
 * scary failure. Encoding that as a class with a `code` plus a `retryable` flag
 * means callers never parse error strings.
 */

/** Error codes the v1 API itself emits (see packages/control-plane/src/http.ts). */
export const API_ERROR_CODES = [
  "invalid_request",
  "unauthenticated",
  "unauthorized",
  "entitlement_denied",
  "forbidden",
  "not_found",
  "method_not_allowed",
  "conflict",
  "rate_limited",
  "internal_error",
] as const;

export type ApiErrorCode = (typeof API_ERROR_CODES)[number];

/** Codes the client synthesises when the failure never reached the API. */
export const CLIENT_ERROR_CODES = ["network_error", "aborted", "invalid_response", "invalid_request"] as const;

export type ClientErrorCode = (typeof CLIENT_ERROR_CODES)[number];

export type DayviseErrorCode = ApiErrorCode | ClientErrorCode;

export interface DayviseApiErrorInit {
  readonly status: number;
  readonly code: string;
  readonly message: string;
  readonly requestId?: string | null;
  readonly body?: unknown;
  readonly retryable?: boolean;
  readonly cause?: unknown;
}

/** HTTP statuses for which a retry is safe and plausible. */
const RETRYABLE_STATUSES = new Set([408, 425, 429, 500, 502, 503, 504]);

export class DayviseApiError extends Error {
  /** HTTP status, or 0 when the request never produced a response. */
  readonly status: number;
  /** Machine-readable code: the server's `error` field, or a client code. */
  readonly code: string;
  /** Correlation id from the edge (`cf-ray`) or the API, when present. */
  readonly requestId: string | null;
  /** Decoded response body when it was JSON, otherwise `null`. */
  readonly body: unknown;
  readonly retryable: boolean;

  constructor(init: DayviseApiErrorInit) {
    super(init.message);
    this.name = "DayviseApiError";
    this.status = init.status;
    this.code = init.code;
    this.requestId = init.requestId ?? null;
    this.body = init.body ?? null;
    this.retryable = init.retryable ?? RETRYABLE_STATUSES.has(init.status);
    if (init.cause !== undefined) this.cause = init.cause;
  }

  /** True when the caller needs to authenticate again. */
  get requiresSignIn(): boolean {
    return this.status === 401;
  }

  /** True when the workspace's plan does not allow the action. */
  get isEntitlementDenied(): boolean {
    return this.status === 402 || this.code === "entitlement_denied";
  }

  /** True when the platform blamed itself rather than the request. */
  get isPlatformFailure(): boolean {
    return this.status >= 500;
  }
}

/**
 * Fallback code for a status whose body carried no `error` field. Mirrors the
 * mapping the API itself uses so both ends of the protocol agree.
 */
export function statusToErrorCode(status: number): DayviseErrorCode {
  switch (status) {
    case 400:
      return "invalid_request";
    case 401:
      return "unauthenticated";
    case 402:
      return "entitlement_denied";
    case 403:
      return "forbidden";
    case 404:
      return "not_found";
    case 405:
      return "method_not_allowed";
    case 409:
      return "conflict";
    case 429:
      return "rate_limited";
    default:
      return status >= 500 ? "internal_error" : "invalid_response";
  }
}

/** Default, human-meaningful message for a status when the API sends none. */
export function statusToMessage(status: number, statusText: string): string {
  if (status === 0) return "the request could not reach Dayvise";
  if (status === 402) return "this action is not included in the current plan";
  if (status >= 500) return "Dayvise could not complete the request";
  return statusText.length > 0 ? statusText : `request failed with status ${status}`;
}

export function isDayviseApiError(error: unknown): error is DayviseApiError {
  return error instanceof DayviseApiError;
}

/** True when an aborted request is the reason a call rejected. */
export function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === "AbortError";
}
